import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import '../styles/CourseForm.css';

const CourseForm = () => {
  const { id } = useParams(); // 수정 모드일 경우 강의 ID
  const isEditMode = !!id;
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'EJU',
    subject: '',
    price: '',
    thumbnail: '',
    isPublished: false
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 인증 및 권한 확인
  useEffect(() => {
    if (!loading && (!isAuthenticated || (user && user.role !== 'admin'))) {
      navigate('/login');
    }
  }, [isAuthenticated, user, loading, navigate]);

  // 수정 모드일 경우 강의 데이터 가져오기
  useEffect(() => {
    if (isEditMode) {
      const fetchCourse = async () => {
        setIsLoading(true);
        try {
          const res = await api.get(`/courses/${id}`);
          const course = res.data;
          setFormData({
            title: course.title,
            description: course.description,
            category: course.category,
            subject: course.subject,
            price: course.price,
            thumbnail: course.thumbnail || '',
            isPublished: course.isPublished
          });
        } catch (err) {
          setError('강의 정보를 불러오는데 실패했습니다.');
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchCourse();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // 가격을 숫자로 변환
      const courseData = {
        ...formData,
        price: parseInt(formData.price, 10)
      };
      
      if (isEditMode) {
        // 강의 수정
        await api.put(`/courses/${id}`, courseData);
        setSuccess('강의가 성공적으로 수정되었습니다.');
      } else {
        // 새 강의 추가
        await api.post('/courses', courseData);
        setSuccess('새 강의가 성공적으로 추가되었습니다.');
        // 폼 초기화
        setFormData({
          title: '',
          description: '',
          category: 'EJU',
          subject: '',
          price: '',
          thumbnail: '',
          isPublished: false
        });
      }
    } catch (err) {
      setError(err.response?.data?.msg || '강의 저장에 실패했습니다.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  return (
    <div className="course-form-container">
      <div className="form-header">
        <h1>{isEditMode ? '강의 수정' : '새 강의 추가'}</h1>
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/admin')}
        >
          뒤로 가기
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <form onSubmit={handleSubmit} className="course-form">
        <div className="form-group">
          <label htmlFor="title">강의 제목 *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="강의 제목을 입력하세요"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="description">강의 설명 *</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            placeholder="강의에 대한 상세 설명을 입력하세요"
            rows="5"
          ></textarea>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="category">카테고리 *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              <option value="EJU">EJU</option>
              <option value="일반입시">일반입시</option>
              <option value="기초">기초</option>
              <option value="고급">고급</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="subject">과목 *</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="예: 일본어, 수학, 과학 등"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="price">가격 (원) *</label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
              placeholder="가격을 입력하세요"
              min="0"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="thumbnail">썸네일 URL</label>
            <input
              type="url"
              id="thumbnail"
              name="thumbnail"
              value={formData.thumbnail}
              onChange={handleChange}
              placeholder="썸네일 이미지 URL을 입력하세요"
            />
          </div>
        </div>
        
        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="isPublished"
            name="isPublished"
            checked={formData.isPublished}
            onChange={handleChange}
          />
          <label htmlFor="isPublished">공개 상태로 설정</label>
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => navigate('/admin')}
          >
            취소
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? '저장 중...' : isEditMode ? '강의 수정' : '강의 추가'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CourseForm; 